import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Icon } from '@capsico/ui';
import { SEARCH_SORTS, SEARCH_SORT_LABELS, type SearchSort } from '@capsico/contracts';
import { useI18n } from '../i18n';
import { cardLabels } from '../i18n/labels';
import { useDeliveryPoint } from '../hooks/useDeliveryPoint';
import { useSearch, type FlagFilter } from '../hooks/useSearch';
import { RestaurantCard } from '../components/RestaurantCard';
import { Button, EmptyState, FilterChip, ListSkeleton, Notice } from '../components/ui';
import type { MessageKey } from '../i18n';

const FLAGS: { flag: FlagFilter; key: MessageKey }[] = [
  { flag: 'veg', key: 'search.filterVeg' },
  { flag: 'pureVeg', key: 'search.filterPureVeg' },
  { flag: 'rating4', key: 'search.filterRating' },
  { flag: 'fast', key: 'search.filterFast' },
  { flag: 'openNow', key: 'search.filterOpen' },
  { flag: 'newOnly', key: 'search.filterNew' },
];

/** Search (C5): search box, sort, filter chips, cuisine facets, result cards. */
export function SearchPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const point = useDeliveryPoint();
  const search = useSearch(point, params.get('q') ?? '');
  const input = useRef<HTMLInputElement>(null);
  const cuisineParam = params.get('cuisine');
  const { setCuisine } = search;

  useEffect(() => {
    if (cuisineParam) setCuisine(cuisineParam);
  }, [cuisineParam, setCuisine]);

  useEffect(() => {
    if (!params.get('q') && !cuisineParam) input.current?.focus();
  }, [params, cuisineParam]);

  const result = search.result;
  const country = result?.area.country ?? null;
  const anyFilter =
    FLAGS.some(({ flag }) => search.filters[flag as keyof typeof search.filters]) ||
    search.filters.cuisine !== null;

  return (
    <div className="min-h-dvh bg-page">
      <header className="sticky top-0 z-30 bg-surface px-4 pt-[max(env(safe-area-inset-top),12px)] pb-2 shadow-[inset_0_-1px_0_var(--color-hair)]">
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label={t('common.back')}
            onClick={() => navigate(-1)}
            className="inline-flex size-9 items-center justify-center text-ink"
          >
            <Icon name="chevron-left" size={20} />
          </button>
          <label className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-xl bg-surface px-3 shadow-[inset_0_0_0_1px_var(--color-field)] focus-within:shadow-[inset_0_0_0_1.5px_var(--color-brand-600)]">
            <Icon name="search" size={18} />
            <input
              ref={input}
              type="search"
              value={search.text}
              onChange={(event) => search.setText(event.target.value)}
              placeholder={t('search.placeholder')}
              aria-label={t('search.placeholder')}
              className="min-w-0 flex-1 bg-transparent text-sm text-ink placeholder:text-ink-3 focus:outline-none"
            />
            {search.text && (
              <button
                type="button"
                aria-label={t('search.clear')}
                onClick={() => search.setText('')}
                className="text-ink-3"
              >
                <Icon name="x" size={16} />
              </button>
            )}
          </label>
        </div>
        <div className="no-scrollbar -mx-4 mt-2 flex items-center gap-2 overflow-x-auto px-4 pb-1">
          <label className="relative inline-flex h-8 shrink-0 items-center gap-1 rounded-full bg-surface pr-2 pl-3 text-xs font-medium text-ink shadow-[inset_0_0_0_1px_var(--color-control)]">
            <Icon name="sliders" size={14} />
            <select
              aria-label={t('search.sort')}
              value={search.sort}
              onChange={(event) => search.setSort(event.target.value as SearchSort)}
              className="appearance-none bg-transparent pr-4 focus:outline-none"
            >
              {SEARCH_SORTS.map((sort) => (
                <option key={sort} value={sort}>
                  {SEARCH_SORT_LABELS[sort]}
                </option>
              ))}
            </select>
            <Icon name="chevron-down" size={14} />
          </label>
          {FLAGS.map(({ flag, key }) => (
            <FilterChip
              key={flag}
              on={Boolean(search.filters[flag as keyof typeof search.filters])}
              onClick={() => search.toggle(flag)}
            >
              {t(key)}
            </FilterChip>
          ))}
          {search.filters.cuisine && (
            <FilterChip on onClick={() => search.setCuisine(null)}>
              {search.filters.cuisine}
              <Icon name="x" size={12} />
            </FilterChip>
          )}
        </div>
      </header>

      {!point ? (
        <EmptyState
          icon="pin"
          title={t('location.choose')}
          action={<Button onClick={() => navigate('/')}>{t('location.choose')}</Button>}
        >
          {t('location.setFirst')}
        </EmptyState>
      ) : !search.active ? (
        <EmptyState icon="search" title={t('search.start')} />
      ) : search.loading ? (
        <div className="px-4 pt-4">
          <ListSkeleton rows={3} tall />
        </div>
      ) : search.failed ? (
        <div className="px-4 pt-4">
          <Notice
            tone="negative"
            supportRef
            title={t('common.wrong')}
            action={
              <Button size="sm" tone="outline" onClick={search.retry}>
                {t('common.retry')}
              </Button>
            }
          />
        </div>
      ) : result && result.results.length === 0 ? (
        <EmptyState
          icon="search"
          title={t('search.noneTitle')}
          action={
            anyFilter ? (
              <Button tone="outline" onClick={search.clearFilters}>
                {t('search.clear')}
              </Button>
            ) : undefined
          }
        >
          {t('search.noneBody')}
        </EmptyState>
      ) : result ? (
        <div className="px-4 pt-3 pb-6">
          {result.cuisines.length > 0 && !search.filters.cuisine && (
            <div className="no-scrollbar -mx-4 mb-3 flex gap-2 overflow-x-auto px-4">
              {result.cuisines.slice(0, 12).map((facet) => (
                <button
                  key={facet.name}
                  type="button"
                  onClick={() => search.setCuisine(facet.name)}
                  className="h-8 shrink-0 rounded-full bg-tile px-3 text-xs text-ink-2"
                >
                  {`${facet.name} · ${facet.count}`}
                </button>
              ))}
            </div>
          )}
          <p className="mb-3 text-micro font-semibold tracking-[0.06em] text-ink-3 uppercase">
            {t('search.results', { count: result.total })}
          </p>
          <div className="space-y-4">
            {result.results.map((outlet) => (
              <RestaurantCard
                key={outlet.outletId}
                outlet={outlet}
                country={country}
                labels={cardLabels(t)}
                onOpen={() => navigate(`/r/${outlet.outletId}`)}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
